import { useState, useEffect, useRef, useCallback } from 'react';
import { Avatar, Drawer, Tooltip, Whisper } from 'rsuite';
import { Close } from '@rsuite/icons';
import { useHistory } from 'react-router-dom';
import './DrawerCollection.scss';
import SuggestionCards from '../SuggestionCards/SuggestionCards';
import {
  ArrowUp,
  Copy,
  Danger,
  Dislike,
  Like1,
  Refresh,
  Refresh2,
  Save2,
  SaveAdd,
  TickCircle,
  Warning2,
} from 'iconsax-react';

import {
  DocumentText,
  Graph,
  Note,
  SearchNormal,
  MessageQuestion,
  ClipboardText,
  DocumentFilter,
  TableDocument,
} from 'iconsax-react';

import { DrawerCollectionSkeleton } from './DrawerCollectionSkeleton';
import MarkdownFormatter from './components/MarkdownFormatter';
import { toast } from 'react-toastify';
import TooltipTurivius from '../../../../../ds-components/components/Tooltips/TooltipTurivius';
import Stars from '../../../../../assets/svgs/new-stars.svg';
import { FloatingUI } from '../../../../../components/ui/float';
import { wait } from '../../../../../utils/wait';
import ChatInputBar from './ChatInputBar';
import { PromptModal } from './PromptSelectorButton';

type DrawerCollectionProps = {
  open: boolean;
  onClose: () => void;
  message: string;
  collectionId?: any;
};

type ChatData = {
  chat_session_id: string;
};

const generateUniqueId = () => Date.now().toString();
const DrawerCollection: React.FC<DrawerCollectionProps> = ({
  open,
  onClose,
  message,
  collectionId,
}) => {
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(true);
  const [inputText, setInputText] = useState('');
  const [userQuestion, setUserQuestion] = useState<string>('');
  const [messageDate, setMessageDate] = useState<string | null>(null);
  const [chatData, setChatData] = useState<ChatData | null>(null);
  const [messages, setMessages] = useState<any[]>([]);
  const [searchHistory, setSearchHistory] = useState<boolean>(false);
  const [copiedId, setCopiedId] = useState<number | null>(null);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const drawerRef = useRef<HTMLDivElement | null>(null);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [disableUserDigite, setDisableUserDigite] = useState(false);
  const [error, setError] = useState<any>(null);
  const [sessionId, setSessionId] = useState<string>(generateUniqueId());
  const [textToCopy, setTextToCopy] = useState<string | undefined>('');
  const [copied, setCopied] = useState(false);

  // Modal de prompt
  const [modalOpen, setModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'list' | 'create' | 'company'>(
    'list',
  );

  // Referencia ao websocket que manda mensagens
  const socketRef = useRef<WebSocket | null>(null);

  // Estados que manipulam os componentes do modal que aparece na parte de cadastro de prompts
  const [promptContent, setPromptContent] = useState('');
  const [promptTitle, setPromptTitle] = useState('');

  const [references, setReferences] = useState({});

  // URLS do web socket
  const ragUrl = process.env.REACT_APP_COLLECTIONS;
  const wsUrl =
    process.env.REACT_APP_COLLECTIONS_WS ||
    `ws://${ragUrl?.replace(/^https?:\/\//, '').replace(/\/$/, '')}/ws/chat/`;

  // Save Prompt
  const handleSavePrompt = (text: string) => {
    setPromptContent(text);
    setPromptTitle('');
    setActiveTab('create');
    setModalOpen(true);
  };

  const connectWebSocketIfNeeded = () => {
    return new Promise<void>((resolve, reject) => {
      if (
        socketRef.current &&
        socketRef.current.readyState === WebSocket.OPEN
      ) {
        resolve(); // já conectado
        return;
      }

      // const wsUrl = `ws://${ragUrl?.replace(/^https?:\/\//, '').replace(/\/$/, '')}/ws/chat/`;
      const socket = new WebSocket(wsUrl);

      socket.onopen = () => {
        console.log('WebSocket conectado');
        socketRef.current = socket;
        resolve();
      };

      socket.onerror = (e) => {
        console.error('Erro ao conectar WebSocket:', e);
        reject(e);
      };

      socket.onmessage = (event) => {
        const data = JSON.parse(event.data);

        if (data.references) {
          setReferences(data.references || {}); // passa isso para o Markdown
        }

        if (data.token) {
          setIsLoading(false);

          setMessages((prev) => {
            const last = prev[prev.length - 1];
            if (last && last.type === 'ai' && !last.done) {
              return [
                ...prev.slice(0, -1),
                { ...last, content: last.content + data.token },
              ];
            } else {
              return [
                ...prev,
                { type: 'ai', content: data.token, done: false },
              ];
            }
          });
        }

        if (data.done) {
          if (data.moderate) {
            setIsLoading(false);
            setMessages((prev) => {
              return [
                ...prev,
                {
                  type: 'ai',
                  content: 'Como posso lhe ajudar ?',
                  done: true,
                  id: '-1', // EXPLANATION: Colocar -1 pq senao o JS entende que null igual qq coisa e deixar o valor copiado kkkk
                },
              ];
            });
          } else {
            setMessages((prev) => {
              const last = prev[prev.length - 1];
              return [
                ...prev.slice(0, -1),
                { ...last, done: true, id: data.message_id },
              ];
            });
          }

          // EXPLANATION: FECHA O WEBSOCKET AQUI
          if (socketRef.current) {
            console.log('WebSocket encerrado após resposta final.');
            socketRef.current.close(1000, 'Resposta final recebida');
          }

          setDisableUserDigite(false);
        }

        if (data.error) {
          console.error('Erro no servidor:', data.error);

          let error_message =
            'Desculpe, algo deu errado. Por favor, tente novamente';
          if (data.moderate) {
            error_message =
              'Você usou uma linguagem inapropriada, infelizmente não podemos responder a esse tipo de pergunta!';
          }

          setMessages((prev) => {
            return [
              ...prev,
              { content: error_message, done: true, type: 'error' },
            ];
          });

          setIsLoading(false);
          //  EXPLANATION: FECHA EM CASO DE ERRO TAMBÉM
          if (socketRef.current) {
            socketRef.current.close(3000, 'Erro no servidor');
          }
        }
      };

      socket.onclose = () => {
        console.log('WebSocket desconectado');
      };

      socketRef.current = socket;
    });
  };

  // useEffect(() => {
  //   if (disableUserDigite == true) {
  //     setDisableUserDigite(false);
  //   } else {
  //     setDisableUserDigite(true);
  //   }
  // }, [isLoading]);

  const scrollToLastMessage = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleScroll = () => {
    if (drawerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = drawerRef.current;
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 50;
      setShowScrollButton(!isAtBottom);
    }
  };

  useEffect(() => {
    const drawer = drawerRef.current;
    if (drawer) {
      drawer.addEventListener('scroll', handleScroll);
      handleScroll();

      return () => {
        drawer.removeEventListener('scroll', handleScroll);
      };
    }
  }, []);

  const handleStopStream = () => {
    if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
      socketRef.current.close(1000, 'Interrompido pelo usuário');
    }
    setIsLoading(false);
    setDisableUserDigite(false); // reabilita o botão
  };

  const userID = localStorage.getItem('user');
  const boardId = collectionId;

  const sendMessageWS = async (
    question: string,
    persona: string,
    model: string,
    search: string,
  ) => {
    setIsLoading(true); // Ativa o carreamento ate o primeiro token
    setDisableUserDigite(true); // desativa o botao
    try {
      await connectWebSocketIfNeeded();

      if (socketRef.current?.readyState === WebSocket.OPEN) {
        const payload = {
          question,
          board_id: boardId,
          user_id: userID,
          persona,
          model,
          search,
        };
        socketRef.current.send(JSON.stringify(payload));
      } else {
        alert('WebSocket não conectado');
      }
    } catch (error) {
      alert('Erro ao abrir conexão WebSocket');
      setIsLoading(false);
      // setDisableUserDigite(true);
    }
  };

  const fetchMessages = async (sessionId: string) => {
    try {
      const response = await fetch(
        `${ragUrl}api/messages/${sessionId}/get_messages/`,
      );
      const data = await response.json();
      setMessages(data);

      if (data.length > 0) {
        setSearchHistory(true);
        const lastMessage = data[data.length - 1];
        formatAndSetDate(lastMessage.created_at);
      } else {
        setSearchHistory(false);
      }
      setTimeout(scrollToLastMessage, 100);
    } catch (error) {
      console.error('Erro ao buscar mensagens:', error);
    }
  };

  const formatAndSetDate = (dateString: string) => {
    const timestamp = Date.parse(dateString);
    const date = new Date(timestamp);

    if (!isNaN(date.getTime())) {
      const formattedDate = `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()} às ${date.getHours()}:${String(date.getMinutes()).padStart(2, '0')}`;
      setMessageDate(formattedDate);
    } else {
      console.error('Data inválida:', dateString);
      setMessageDate('Data inválida');
    }
  };

  useEffect(() => {
    if (open) {
      setIsLoading(true);
      fetch(`${ragUrl}api/conversations/${boardId}/last_chat/`)
        .then((response) => response.json())
        .then((data) => {
          setChatData(data);
          setIsLoading(false);
          fetchMessages(data.chat_session_id).then(() => {
            setIsLoading(false);
            scrollToLastMessage();
            setShowScrollButton(false);
          });
        })
        .catch((error) => {
          console.error('Erro ao buscar dados da conversa:', error);
          setIsLoading(false);
        });
    }
  }, [open]);

  // EXPLANATION: Esse hook controla o momento em que uma nova mensagem esta sendo gerada
  // e caso o conteudo escpae da tela ele mostra a setinha
  useEffect(() => {
    if (!drawerRef.current) return;

    const observer = new MutationObserver(() => {
      const { scrollTop, scrollHeight, clientHeight } = drawerRef.current!;
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 50;
      setShowScrollButton(!isAtBottom);
    });

    observer.observe(drawerRef.current, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    return () => observer.disconnect();
  }, []);

  const allSuggestions = [
    'Qual o entendimento predominante sobre o tema tratado nesses acórdãos?',
    'Houve alguma alteração na tendência de posicionamento dos julgadores?',
    'Nesses acórdãos, quais os pequenos detalhes que fazem toda a diferença?',
    'Qual precedente foi mais citado nessas decisões?',
    'Quais leis e artigos foram mais mencionados?',
    'Quais julgados mais recentes podem impactar outros casos semelhantes?',
    'Quais os principais temas secundários discutidos nesses acórdãos?',
    'Quais os principais argumentos vencedores?',
    'Qual precedente mais diverge dos demais no entendimento do tema? Qual o relator dele?',
    'Quais são os pontos de divergência mais frequentes entre os julgadores nesses acórdãos?',
    'Quais decisões seguiram uma linha de argumentação inovadora?',
    'Existe alguma diferença de entendimento entre os Tribunais?',
  ];

  const profile = JSON.parse(localStorage.getItem('profile') || '{}');

  const getFirstName = (data: string | undefined) => {
    if (data !== undefined) {
      if (data.split(' ').length >= 2) {
        return `${data.split(' ')[0]} ${data.split(' ')[1].substring(0, 1)}`;
      } else {
        return data;
      }
    } else {
      return 'Turivius';
    }
  };

  const [suggestions, setSuggestions] = useState<string[]>(
    allSuggestions.slice(0, 6),
  );
  const [currentIndex, setCurrentIndex] = useState<number>(6);

  const reloadSuggestions = () => {
    let nextIndex = currentIndex;

    if (nextIndex >= allSuggestions.length) {
      nextIndex = 0;
    }

    const newSuggestions = allSuggestions.slice(nextIndex, nextIndex + 6);
    setSuggestions(newSuggestions);

    setCurrentIndex(nextIndex + 6);
  };

  const handleClose = () => {
    onClose();
    history.push({
      pathname: window.location.pathname,
      search: '?openDrawer=false',
    });
  };

  const handleCopyClick = (id: number, content: string) => {
    navigator.clipboard
      .writeText(content)
      .then(() => {
        setCopiedId(id);
        setTimeout(() => {
          setCopiedId(null);
        }, 3000);
      })
      .catch((err) => {
        console.error('Erro ao copiar para a área de transferência: ', err);
      });
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setInputText(e.target.value);
  };

  const handleInsertPrompt = (text: string) => {
    setInputText(text);
  };

  // EXPLANATION: Essa é a função que faz a rolagem da tela
  // const scrollToUserMessageTop = () => {
  //   if (drawerRef.current) {
  //     const container = drawerRef.current;

  //     container.scrollTo({
  //       top: container.scrollHeight - container.clientHeight,
  //       behavior: 'smooth',
  //     });
  //   }
  // };

  const scrollToUserMessageTop = () => {
    const container = drawerRef.current;
    if (!container) return;

    const lastUserMessage = container.querySelector(
      '.card-container-end:last-child',
    ) as HTMLElement;
    if (!lastUserMessage) return;

    const messageTop = lastUserMessage.offsetTop;
    const messageHeight = lastUserMessage.offsetHeight;
    const maxScroll = container.scrollHeight - container.clientHeight;

    // Calcula o scroll necessário para alinhar o topo da mensagem
    let targetScroll = messageTop;

    // Se a mensagem for maior que o espaço visível, ajustar para que o final dela ainda apareça
    if (messageTop + messageHeight > maxScroll) {
      targetScroll = Math.min(messageTop, maxScroll);
    }

    container.scrollTo({
      top: targetScroll,
      behavior: 'smooth',
    });
  };

  // const scrollToUserMessageTop = () => {
  //   const container = drawerRef.current;
  //   if (!container) return;

  //   const lastUserMessage = container.querySelector(
  //     '.card-container-end:last-child',
  //   ) as HTMLElement;
  //   if (!lastUserMessage) return;

  //   const messageTop = lastUserMessage.offsetTop;
  //   const messageHeight = lastUserMessage.offsetHeight;
  //   const containerHeight = container.clientHeight;
  //   const maxScroll = container.scrollHeight - containerHeight;

  //   // Se a mensagem for maior que a viewport, precisamos mostrar o final dela
  //   let targetScroll = messageTop;

  //   if (messageHeight > containerHeight) {
  //     // Rolar até a parte da mensagem que garante o final visível
  //     targetScroll = messageTop;
  //   } else if (
  //     messageTop + messageHeight >
  //     container.scrollTop + containerHeight
  //   ) {
  //     // A mensagem é pequena, mas está parcialmente fora da tela
  //     targetScroll = Math.min(messageTop, maxScroll);
  //   }

  //   container.scrollTo({
  //     top: targetScroll,
  //     behavior: 'smooth',
  //   });
  // };

  const handleSubmit = (params: {
    inputText: string;
    persona: string;
    model: string;
    search: string;
  }) => {
    if (inputText.trim()) {
      setMessages((prev) => [
        ...prev,
        {
          type: 'human',
          created_at: Date.now(),
          content: inputText,
        },
      ]);

      // sendMessage(params.inputText, params.persona, params.model, params.search);
      sendMessageWS(
        params.inputText,
        params.persona,
        params.model,
        params.search,
      );

      // EXPLANATION: esse trecho de código faz o componente rolar para última mensagem
      setTimeout(scrollToUserMessageTop, 100);

      setUserQuestion(inputText);
      setInputText('');
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    // setMessages((prev) => [
    //   ...prev,
    //   {
    //     type: 'human',
    //     created_at: Date.now(),
    //     content: suggestion,
    //   },
    // ]);
    // sendMessage(suggestion);
    setInputText(suggestion);
    // setUserQuestion(suggestion);
  };

  const handleClearChat = async () => {
    if (!chatData) {
      alert('Nenhum chat para limpar.');
      return;
    }

    const sessionId = chatData.chat_session_id;

    try {
      const response = await fetch(
        `${ragUrl}api/messages/${sessionId}/reset_chat/`,
        {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
        },
      );

      if (!response.ok) {
        const errorMessage = await response.text();
        throw new Error(`Erro ao limpar o chat: ${errorMessage}`);
      }
      if (response) {
        setMessages([]);
        // toast.success('Chat limpo com sucesso');
      }
    } catch (error) {
      console.error('Erro ao limpar o chat:', error);
      alert('Ocorreu um erro ao limpar o chat. Tente novamente.');
    }
  };

  const handleLikeClick = async (
    userID: any,
    react: boolean,
    messageId: number,
  ) => {
    const data = {
      user_id: userID,
      liked: react,
      message_id: messageId,
    };

    console.log(userID);
    try {
      const response = await fetch(`${ragUrl}api/messages/react/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (response.ok) {
        const result = await response.json();
        toast.success('Agradeçemos pelo seu feedback!');

        // EXPLANATION: Atualiza o estado local para refletir o like/dislike
        setMessages((prevMessages) =>
          prevMessages.map((msg) =>
            msg.id === messageId
              ? {
                  ...msg,
                  // like_count: react ? 1 : msg.like_count || 0,
                  // dislike_count: !react ? 1 : msg.dislike_count || 0,
                  like_count: react ? 1 : 0,
                  dislike_count: react ? 0 : 1,
                }
              : msg,
          ),
        );
      } else {
        toast.error('Houve algum problema ao registrar seu feedback.');
      }
    } catch (error) {
      toast.error('Houve algum problema ao registrar seu feedback.');
    }
  };

  const handleNewConversation = () => {
    setMessages([]);
    setSessionId(generateUniqueId());
    setDisableUserDigite(false);
    setError(null);
    handleClearChat();
  };

  const handleCopy = useCallback(async () => {
    navigator.clipboard.writeText(textToCopy || '');
    setCopied(true);
    await wait(5000);
    setCopied(false);
    setTextToCopy('');
  }, [textToCopy]);

  return (
    // Esse size controla o tamanho do modal do RAG
    <Drawer open={open} onClose={onClose} placement="right" size="70%">
      {modalOpen && (
        <PromptModal
          open={open}
          onClose={() => setModalOpen(false)}
          onInsertPrompt={handleInsertPrompt}
          promptContent={promptContent}
          setPromptContent={setPromptContent}
          promptTitle={promptTitle}
          setPromptTitle={setPromptTitle}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
        />
      )}
      <div className="sticky top-0 z-50 bg-white flex justify-end items-center gap-4 px-6 py-4 border-b shadow-sm">
        <div className="title-modal-search-history">
          <img
            className="icon-stars"
            src="/stars-group.png"
            alt="Ícone de estrelas"
          />
          <h1>GPTuri</h1>
          <span>beta</span>
        </div>
        <button
          className="new flex items-center gap-2"
          onClick={handleNewConversation}
        >
          <Refresh2 size="20" color="#475467" />
          <span>Nova conversa</span>
        </button>
        <TooltipTurivius
          text="Não se preocupe, seu chat continua salvo"
          position="left"
        >
          <button className="close-button-search-history" onClick={handleClose}>
            <Close className="close-icon-modal" />
            <p>Fechar</p>
          </button>
        </TooltipTurivius>
      </div>
      <div
        ref={drawerRef}
        className={
          messages.length > 0 ? 'container-history' : 'container-modal'
        }
        style={{ paddingBottom: '220px' }}
      >
        {messages.length === 0 ? (
          <>
            {/* <div className="title-modal">
              <img className="icon-stars" src={Stars} alt="Ícone de estrelas" />
              <h1>GPTuri</h1>
              <span>beta</span>
            </div> */}

            <span className="gradient-text mt-[100px]">
              Qual serviço deseja receber?
            </span>
            <p className="mb-[]">
              Selecione abaixo uma entrega pronta ou descreva sua demanda
              personalizada.
            </p>
          </>
        ) : (
          <>
            <div className="card-search-history">
              <FloatingUI getTextSelected={setTextToCopy}>
                <>
                  <button
                    onClick={handleCopy}
                    disabled={copied}
                    className="gap-2 flex items-center px-3 py-3 rounded border border-[#344054] bg-[#101828] hover:opacity-100"
                    style={{ color: 'white' }}
                  >
                    {copied ? (
                      <>
                        <TickCircle size={20} color="white" />
                        Copiado
                      </>
                    ) : (
                      <>
                        <Copy size={20} color="white" />
                        Copiar
                      </>
                    )}
                  </button>
                  <div className="left-1/2 absolute bottom-0 transform -translate-x-1/2 translate-y-1/2 rotate-45 w-4 h-4 bg-[#101828] border-r border-b border-solid border-[#344054]" />
                </>
              </FloatingUI>
              {messages.map((item) => (
                <div
                  key={item.id}
                  className={`card-container ${item.type === 'human' ? 'card-container-end' : 'card-container-start'}`}
                >
                  {item.type === 'human' ? (
                    <>
                      <div className="relative group card-user">
                        {/* Conteúdo da mensagem */}
                        <span className="selection:bg-[#FDFF75] text-[18px]">
                          <MarkdownFormatter markdownText={item.content} />
                        </span>

                        {/* Ícones aparecem abaixo da mensagem com fade + slide-up */}
                        <div className="absolute right-0 bottom-0 translate-y-2 -translate-x-2 gap-2 mt-2 opacity-0 group-hover:opacity-100 group-hover:translate-y-8 transition-all duration-300 ease-in-out">
                          <TooltipTurivius text="Copiar" position="top">
                            <button
                              onClick={() =>
                                handleCopyClick(item.id, item.content)
                              }
                              className="p-1 mr-4 rounded hover:bg-gray-200"
                            >
                              <Copy size={16} color="#475467" />
                            </button>
                          </TooltipTurivius>

                          <TooltipTurivius text="Salvar prompt" position="top">
                            <button
                              onClick={() => handleSavePrompt(item.content)}
                              className="p-1 rounded hover:bg-gray-200"
                            >
                              <SaveAdd size={16} color="#475467" />
                            </button>
                          </TooltipTurivius>
                        </div>
                      </div>
                    </>
                  ) : // <div className="card-user">
                  //   <span className="selection:bg-[#FDFF75] text-[18px]">
                  //     <MarkdownFormatter markdownText={item.content} />
                  //   </span>
                  //   <div className="mt-4 flex justify-start">
                  //     <button
                  //       onClick={() => handleSavePrompt(item.content)}
                  //       className="flex gap-3 items-center text-sm text-blue-200 hover:text-blue-600"
                  //     >
                  //       <Save2 size={14}></Save2>
                  //       Salvar prompt
                  //     </button>
                  //   </div>
                  // </div>
                  item.type === 'error' ? (
                    <div className="error-card-rag">
                      <div className="error-message-rag">
                        <Danger size={20} color="#F04438" />
                        <span>
                          Desculpe, algo deu errado. Por favor, tente novamente
                        </span>
                      </div>
                      <button
                        className="retry-button-rag"
                        onClick={async () => {
                          setIsLoading(true);
                          try {
                            await fetchMessages(item.chat_session_id);
                            setTimeout(() => {
                              setIsLoading(false);
                            }, 4000);
                          } catch (error) {
                            setIsLoading(false);
                          }
                        }}
                      >
                        <Refresh size={18} color="#344054" />
                        Repetir
                      </button>
                    </div>
                  ) : (
                    <div className="card-ia-answers">
                      <div className="selection:bg-[#FDFF75] text-[18px]">
                        <MarkdownFormatter
                          markdownText={item.content}
                          referenceLinks={item.references || references}
                        />
                      </div>

                      <div className="buttons-footer-rag">
                        <div className="copy-reload-buttons">
                          <TooltipTurivius text="copiar" position="top">
                            <button
                              className={`button-copy ${copiedId === item.id ? 'copied' : ''}`}
                              onClick={() =>
                                handleCopyClick(item.id, item.content)
                              }
                            >
                              {copiedId === item.id ? (
                                <TickCircle size="18" color="#12b76a" />
                              ) : (
                                <Copy size="18" color="#475467" />
                              )}
                              {copiedId === item.id ? 'Copiado' : 'Copiar'}
                            </button>
                          </TooltipTurivius>

                          {item.id && (
                            <>
                              <TooltipTurivius
                                text="Gerar novamente "
                                position="top"
                                style={{ marginLeft: '-60px' }}
                              >
                                <button
                                  className="reload-button-history"
                                  onClick={async () => {
                                    setIsLoading(true);
                                    try {
                                      await fetchMessages(item.chat_session_id);
                                      setTimeout(() => {
                                        setIsLoading(false);
                                      }, 4000);
                                    } catch (error) {
                                      console.error(
                                        'Erro ao carregar mensagens',
                                        error,
                                      );
                                      setTimeout(() => {
                                        setIsLoading(false);
                                      }, 4000);
                                    }
                                  }}
                                  disabled={isLoading}
                                >
                                  <Refresh size="18" color="#475467" />
                                </button>
                              </TooltipTurivius>
                              <TooltipTurivius
                                text="Gostei"
                                position="top"
                                style={{ marginLeft: '-20px' }}
                              >
                                <button
                                  className="button-like"
                                  onClick={() => {
                                    handleLikeClick(userID, true, item.id);
                                  }}
                                >
                                  <Like1
                                    size="18"
                                    color={
                                      item.like_count > 0
                                        ? '#0d91fe'
                                        : '#475467'
                                    }
                                  />
                                </button>
                              </TooltipTurivius>
                              <TooltipTurivius
                                text="Não gostei"
                                position="top"
                                style={{ marginLeft: '-35px' }}
                              >
                                <button
                                  className="button-dislike"
                                  onClick={() => {
                                    handleLikeClick(userID, false, item.id);
                                  }}
                                >
                                  <Dislike
                                    size="18"
                                    color={
                                      item.dislike_count > 0
                                        ? '#FF0000'
                                        : '#475467'
                                    }
                                  />
                                </button>
                              </TooltipTurivius>
                            </>
                          )}
                        </div>
                        {
                          <div className="model-info-footer-rag">
                            {/* {item.model_name && (
                              <span>
                                Resposta gerada com{' '}
                                <strong> {item.model_name + ' '} </strong>{' '}
                              </span>
                            )} */}

                            {/* {item.field_source && (
                              <span>
                                e pesquisa {'  '}
                                <span className="model-link-rag">
                                  {item.field_source}
                                </span>
                              </span>
                            )} */}
                            {/* {item.persona && (
                              <span>
                                {' '}
                                <span> como advogado</span>
                                <span className="model-link-rag">
                                  {' '}
                                  {item.persona}
                                </span>
                              </span>
                            )} */}
                          </div>
                        }
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
        {isLoading && <DrawerCollectionSkeleton visible={isLoading} />}
        {messages.length === 0 ? (
          <>
            {/* <div
              className="button-reload-history"
              style={{ position: 'relative' }}
            >
              <TooltipTurivius text="Gere novas sugestões" position="top">
                <button onClick={reloadSuggestions} className="reload-button">
                  <h2>Sugestões</h2>
                  <img
                    className="icon-reload"
                    src="/Vector.png"
                    alt="ícone de carregar"
                  />
                </button>
              </TooltipTurivius>
            </div> */}
            <div className="card-container">
              {suggestions.length > 0 && (
                <SuggestionCards
                  // key={index}
                  // text={text}
                  onClick={handleSuggestionClick}
                />
              )}
            </div>
          </>
        ) : (
          ''
        )}

        {showScrollButton && (
          <button className="button-history" onClick={scrollToLastMessage}>
            <img src="/arrow-down.png" alt="Ícone do botão" />
          </button>
        )}

        <div
          style={{
            position: 'absolute',
            bottom: 0,
            width: '98%',
            left: '57%',
            transform: 'translateX(-50%)',
            maxWidth: '1800px', // você pode ajustar isso
            backgroundColor: '#fff',
            padding: '0px 20px 18px 16px',
            zIndex: 10,
            borderRadius: '16px',
          }}
        >
          <ChatInputBar
            value={inputText}
            onChange={handleInputChange}
            disabled={disableUserDigite}
            onClick={inputText ? handleSubmit : () => setInputText('')}
            onStop={handleStopStream}
            setActiveTab={setActiveTab}
            setModalOpen={setModalOpen}
            modalOpen={modalOpen}
            handleInsertPrompt={handleInsertPrompt}
          />
        </div>
        <div
          style={{
            position: 'absolute',
            bottom: 0,
            width: '98%',
            left: '49%',
            transform: 'translateX(-50%)',
            maxWidth: '1800px', // você pode ajustar isso
            padding: '4px 16px',
            zIndex: 10,
            borderRadius: '16px',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <span className="footer-warning my-1 font-[#475467]">
            O GPTuri pode falhar, confirme as informações essenciais.
          </span>
        </div>

        <div ref={messagesEndRef} />
        {messages.length > 0 && <div className="spacer" />}
      </div>
    </Drawer>
  );
};

export default DrawerCollection;
